import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AppImage from '../../../components/AppImage';
import DemoVideoFrame from '../../../components/home/DemoVideoFrame';


const FeaturesSection = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  // Map active feature index -> screenshot path. Missing images will gracefully
  // fall back via AppImage to /assets/images/no_image.png
  const featureImageMap = {
    0: '/assets/images/Features_image_categorization.png',
    1: '/assets/images/Features_image_CT600_filing.png',
    2: '/assets/images/Features_image_chat.png',
  };
  const currentImageSrc = featureImageMap[activeFeature] || '/assets/images/no_image.png';

  // Video sources per feature ensure we only swap the AI-Assisted Tax Filing demo
  const defaultFeatureVideo = '/assets/videos/OOP_feature_v003.mp4';
  const featureVideoMap = {
    0: '/assets/videos/Auto_categorize_row2.mp4',
    1: '/assets/videos/CT600_AI_Filing_v002.mp4',
    2: '/assets/videos/Context_Aware_chat_v002.mp4',
  };
  const currentVideoSrc = featureVideoMap[activeFeature] || defaultFeatureVideo;

  const featureVideoDescription = {
    0: 'Watch Neevii automatically categorize incoming transactions in real time',
    1: 'Guided CT600 filing workspace',
    2: 'Your AI accountant with full context of your accounts',
  };

  const features = [
    {
      id: 0,
      title: "Smart Categorization",
      description: "AI automatically categorizes transactions with 99.7% accuracy, learning from your business patterns.",
      icon: "Brain",
      color: "text-blue-400",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20",
      demo: {
        type: 'list',
        title: "Smart Categorization",
        items: [
          { label: "Office Supplies", amount: "$127.50", category: "Business Expenses" },
          { label: "Client Lunch", amount: "$89.25", category: "Meals & Entertainment" },
          { label: "Software License", amount: "$299.00", category: "Technology" }
        ],
        processing: '0.3 seconds'
      }
    },
    {
      id: 1,
      title: "AI-Assisted Tax Filing",
      description: "Calculate your returns with our hybrid computation engine, compliance monitoring, and AI chat assistance to make filings a painless procedure for corporation, self assessment, and VAT.",
      icon: "Calculator",
      color: "text-amber-400",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/20",
      demo: {
        type: 'image',
        title: "AI Tax Filing Workspace",

        imageSrc: "/assets/images/Features_image_CT600_filing.png",
        footnote: "Corporation • Self Assessment • VAT • Compliance monitoring",
        processing: "AI-assisted review in seconds"
      }
    },
    {
      id: 2,
      title: "Context-Aware AI Chat",
      description: "By working safely within your account, Neevii can answer everything from filing deadlines, helping you categorize an expense, to smart, practical tax-saving advice. The AI accountant that is always available to help.",
      icon: "MessageSquare",
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/20",
      demo: {
        type: 'image',
        title: "AI Chat Assistant",
        imageSrc: "/assets/images/Features_image_chat.png",
        footnote: "Filing Deadlines • Expense Help • Tax-Saving Advice",
        processing: "Always available to help"
      }
    }
  ];

  const handleFeatureClick = (index) => {
    setActiveFeature(index);
  };

  const handleLearnMore = () => {
    navigate('/subscription');
  };

  return (
    <section id="features" className="py-20 bg-background">
      <div className="max-w-7xl xl:max-w-[1600px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-card/50 backdrop-blur-sm border border-border rounded-full px-4 py-2">
            <Icon name="Zap" size={16} className="text-brand-accent" />
            <span className="text-sm text-muted-foreground font-medium">
              Core Features
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold text-foreground">
            Intelligence that works
            <span className="gradient-text block">for your business</span>
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Three powerful AI capabilities that transform how you handle accounting,
            saving time while improving accuracy and compliance.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid lg:grid-cols-[minmax(260px,_1fr)_minmax(0,_1.7fr)] xl:grid-cols-[minmax(300px,_1fr)_minmax(0,_1.7fr)] 2xl:grid-cols-[minmax(320px,_1fr)_minmax(0,_1.7fr)] gap-10 lg:gap-12 items-start">
          {/* Feature Cards */}
          <div className="space-y-6 lg:justify-self-start lg:mt-[60px]">
            {features?.map((feature, index) => (
              <div
                key={feature?.id}
                onClick={() => handleFeatureClick(index)}
                className={`p-6 rounded-2xl border cursor-pointer transition-all duration-300 depth-layer ${
                  activeFeature === index
                    ? `${feature?.bgColor} ${feature?.borderColor} shadow-brand-interactive`
                    : 'bg-card/50 border-border hover:border-primary/20'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-xl ${feature?.bgColor} ${feature?.color}`}>
                    <Icon name={feature?.icon} size={24} />
                  </div>

                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl font-semibold text-foreground">
                      {feature?.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {feature?.description}
                    </p>

                    {activeFeature === index && (
                      <div className="pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleLearnMore();
                          }}
                          iconName="ArrowRight"
                          iconPosition="right"
                          className="text-primary hover:text-primary/80"
                        >
                          View pricing
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className={`transition-transform duration-200 ${
                    activeFeature === index ? 'rotate-90' : ''
                  }`}>
                    <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Demo Preview */}
          <div className="lg:pl-0 xl:pl-2">
            <div className="bg-card/80 backdrop-blur-md rounded-2xl p-6 lg:p-8 min-h-[420px] flex flex-col">
              <div className="space-y-6 flex-1">
                <div className="space-y-3">
                  {/* Screenshot only (no frame). Edge-to-edge with no bezel cropping */}
                  <div className="relative w-full max-w-none bg-transparent self-start">
                    <DemoVideoFrame
                      className="w-full feature-fade-in"
                      src={currentVideoSrc}
                    />

                    <div className="relative w-full lg:w-[1230px] max-w-none rounded-2xl ring-2 ring-yellow-400" style={{ aspectRatio: '1840 / 1070', boxShadow: '0 0 30px rgba(250, 204, 21, 0.35)', display: 'none' }}>
                      {/* CSS yellow frame via ring+glow; image underlay removed */}
                      {/* Screenshot overlay */}
                      <AppImage
                        key={currentImageSrc}
                        src={currentImageSrc}
                        alt="Feature screenshot"
                        className="absolute inset-0 h-full w-full object-contain feature-fade-in"
                        style={{ top: '0%', left: '0%' }}
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${features?.[activeFeature]?.bgColor} ${features?.[activeFeature]?.color}`}>
                        <Icon name={features?.[activeFeature]?.icon} size={20} />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-foreground">
                          {features?.[activeFeature]?.demo?.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {featureVideoDescription[activeFeature] || 'Realtime insights powered by Neevii AI'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-border/80" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes featureFadeIn { from { opacity: 0 } to { opacity: 1 } }
        .feature-fade-in { animation: featureFadeIn .45s ease-out both; }
      `}</style>
    </section>
  );
};

export default FeaturesSection;